import { useEffect } from 'react';

import { snackbar } from '@/_shared';

import type { PickTicketsItemCreateFormSchema } from './schema';
import { useCreateFormDefinition } from './use-create-form-definition';

import { useCreateMutation } from '../../../apis';
import type { PickTicketsItemFormSchema } from '../../../pick-ticket-items-schemas';
import { isKit } from '../helper';

export type AddInventoryKitItemModalProps = {
  open: boolean;
  title: string;
  itemCount: number;
  pickTicketId: number;
  addedKitItems: number[];
  onClose: () => void;
  handleInventoryKitItemCreate: (v: PickTicketsItemFormSchema[]) => void;
};

export function useAddPickTicketsItems({
  open,
  itemCount,
  pickTicketId,
  addedKitItems,
  handleInventoryKitItemCreate,
  onClose,
}: AddInventoryKitItemModalProps) {
  const { isPending } = useCreateMutation();

  const form = useCreateFormDefinition(pickTicketId);
  const inventory_and_kit = form.watch('inventory_and_kit');

  const onSubmit = async (dto: PickTicketsItemCreateFormSchema) => {
    const { items } = dto;
    if (!items) {
      return;
    }
    const itemsToSave = items
      .filter((item) => item.selected)
      .map((item, i) => ({
        ...item,
        id: -1 * ((item.inventory?.id ?? 1) + new Date().getTime()),
        inventory_id: item.inventory?.id ?? -1,
        sort_order: itemCount + 1 + i,
      }));

    await handleInventoryKitItemCreate(itemsToSave);

    form.reset();
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  useEffect(() => {
    if (!inventory_and_kit) {
      form.setValue('items', [], {
        shouldDirty: true,
      });
      return;
    }

    const isKitObj = isKit(inventory_and_kit);

    const items =
      (isKitObj
        ? inventory_and_kit.inventory_kit_items?.map((item) => item.inventory).filter((item) => !!item)
        : [inventory_and_kit]) ?? [];

    // Merge with existing items instead of overwriting to keep old items intact
    const prevItems = form.getValues('items') ?? [];
    const existingCurrentIds = new Set(prevItems.map((item) => item.inventory_id));
    const alreadyAddedCurrentItemFound = items.some((item) => existingCurrentIds.has(item.id));

    const existingKitItemsIds = new Set([...addedKitItems]);
    const alreadyAddedKitItemFound = items.some((item) => existingKitItemsIds.has(item.id));

    const newItemsOnly = items
      .filter((item) => !existingCurrentIds.has(item.id) && !existingKitItemsIds.has(item.id))
      .map((item, i) => {
        const hasStock = (item.current_stock ?? 0) > 0;
        return {
          selected: hasStock,
          inventory: item,
          inventory_id: item.id,
          id: -1 * item.id,
          sort_order: itemCount + prevItems.length + 1 + i,
          pick_ticket_id: pickTicketId,
          quantity: 1,
          disabled: !hasStock,
        };
      });

    const mergedItems = [...prevItems, ...newItemsOnly];

    form.setValue('items', mergedItems, {
      shouldDirty: true,
    });

    if (alreadyAddedCurrentItemFound) {
      if (isKitObj) {
        snackbar.error('Some items in the selected kit have already been added to the current list.');
      } else {
        snackbar.error('The selected item has already been added to the current list.');
      }
    } else if (alreadyAddedKitItemFound) {
      if (isKitObj) {
        snackbar.error('Some items in the selected kit have already been added to the pick ticket.');
      } else {
        snackbar.error('The selected item has already been added to the pick ticket.');
      }
    }
  }, [form, inventory_and_kit, itemCount, pickTicketId, addedKitItems]);

  console.log(form.formState.errors);

  return {
    onSubmit,
    form,
    isPending,
    onClose: handleClose,
  };
}
